import React, { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { getCourseModulestDataApi, addCourseModuleApi, deleteCourseModuleApi, updateCourseModuleApi } from "Services/allApi";
import { MdDelete, MdUpload } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Modules = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const cardTitle = location.state?.cardTitle || "Default Title";
  const [courseModules, setCourseModules] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModuleId, setDeleteModuleId] = useState(null); 
  const [newModule, setNewModule] = useState({
    indexNo: "",
    heading: "",
    description: "",
  });

  useEffect(() => {
    const fetchModules = async () => {
      const response = await getCourseModulestDataApi(id);
      if (response.error) {
        console.error("Error fetching course modules:", response.message);
      } else {
        setCourseModules(response.data || []);
      }
    };

    fetchModules();
  }, [id]);

  const handleEditClick = (id) => {
    navigate(`/admin/edit-courses/${id}`);
  };

  const handleProgramClick = (id) => {
    navigate(`/admin/program-courses/${id}`, { state: { cardTitle: location.state?.cardTitle } });
  };

  const handleSupportClick = (id) => {
    navigate(`/admin/support-courses/${id}`, { state: { cardTitle: location.state?.cardTitle } });
  };
  
  const handleSection1Click = (id) => {
    navigate(`/admin/section1-courses/${id}`, { state: { cardTitle: location.state?.cardTitle } });
  };

  const handleSection2Click = (id) => {
    navigate(`/admin/section2-courses/${id}`, { state: { cardTitle: location.state?.cardTitle } });
  };

  const handleInputChange = (index, field, e) => {
    const updatedModules = [...courseModules];
    updatedModules[index][field] = e.target.value;
    setCourseModules(updatedModules);
  };

  const handleAddModule = async () => {
    if (!newModule.indexNo || !newModule.heading || !newModule.description) {
      toast.error("Please fill in all fields.");
      return;
    }

    const courseId = courseModules.length > 0 ? courseModules[0].course : null;
    if (!courseId) {
      toast.error("Course ID not found. Cannot add module.");
      return;
    }

    const moduleData = {
      index: newModule.indexNo,
      heading: newModule.heading,
      description: newModule.description,
      course: courseId,
    };

    const response = await addCourseModuleApi(moduleData);

    if (response.error) {
      toast.error("Failed to add module. Please try again.");
    } else {
      toast.success("Module added successfully.");
      setCourseModules((prevModules) => [
        ...prevModules,
        { ...moduleData, id: response.data.id },
      ]);
      setNewModule({ indexNo: "", heading: "", description: "" });
      setIsModalOpen(false);
    }
  };

  const handleDeleteModule = async () => {
    const moduleId = deleteModuleId;
    const response = await deleteCourseModuleApi(moduleId);
    if (response.error) {
      toast.error("Failed to delete the module. Please try again.");
    } else {
      toast.success("Module deleted successfully.");
      setCourseModules((prevModules) =>
        prevModules.filter((module) => module.id !== moduleId)
      );
      setDeleteModuleId(null); 
    }
  };

  const handleSaveModuleChanges = async (moduleId) => {
    const updatedModules = [...courseModules];
    const moduleIndex = updatedModules.findIndex((module) => module.id === moduleId);
    const moduleToSave = updatedModules[moduleIndex];

    const updatedData = {
      index: moduleToSave.index,
      heading: moduleToSave.heading,
      description: moduleToSave.description,
    };

    const response = await updateCourseModuleApi(moduleId, updatedData);

    if (response.error) {
      toast.error("Failed to save module. Please try again.");
    } else {
      toast.success("Module updated successfully.");
      setCourseModules((prevModules) =>
        prevModules.map((module) =>
          module.id === moduleId ? { ...module, ...updatedData } : module
        )
      );
    }
  };

  return (
    <>
      <div className="w-full max-w-6xl mx-auto p-8 bg-white shadow-lg rounded-lg  dark:!bg-navy-700 dark:text-white">
        <h2 className="text-4xl font-semibold mb-6">{cardTitle}</h2>
        <div className="flex mb-6 justify-between">
          <div className="space-x-4">
            <button
              onClick={() => handleEditClick(id)}
              className="bg-gray-500 text-white px-4 py-2 rounded-md dark:bg-gray-700"
            >
              Home
            </button>
            <button className="bg-purple-500 text-white px-4 py-2 rounded-md dark:bg-purple-600">
              Modules
            </button>
            <button onClick={() => handleProgramClick(id)} className="bg-gray-500 text-white px-4 py-2 rounded-md dark:bg-gray-700">
              Program
            </button>
            <button onClick={() => handleSupportClick(id)} className="bg-gray-500 text-white px-4 py-2 rounded-md dark:bg-gray-700">
              Support
            </button>
            <button onClick={() => handleSection1Click(id)} className="bg-gray-500 text-white px-4 py-2 rounded-md dark:bg-gray-700">
              Section 1
            </button>
            <button onClick={() => handleSection2Click(id)} className="bg-gray-500 text-white px-4 py-2 rounded-md dark:bg-gray-700">
              Section 2
            </button>
          </div>
          <div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-purple-800 hover:bg-green-600 text-white px-4 py-2 rounded-md"
            >
              Add Modules
            </button>
          </div>
        </div>

        <h2 className="text-3xl font-semibold mb-6">Modules</h2>

        {courseModules.map((module, index) => (
          <div
            key={module.id}
            className="mb-6 p-6 border border-gray-200 rounded-lg shadow-md dark:border-gray-600 dark:bg-[#0b14374d]"
          >
            <h3 className="text-xl font-bold mb-4">{module.heading}</h3>

            <div className="mb-4">
              <label
                htmlFor={`indexNo-${module.id}`}
                className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300"
              >
                Index No
              </label>
              <input
                type="text"
                id={`indexNo-${module.id}`}
                value={module.index || ""}
                onChange={(e) => handleInputChange(index, "index", e)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white dark:focus:ring-indigo-400"
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor={`heading-${module.id}`}
                className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300"
              >
                Heading
              </label>
              <input
                type="text"
                id={`heading-${module.id}`}
                value={module.heading}
                onChange={(e) => handleInputChange(index, "heading", e)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white dark:focus:ring-indigo-400"
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor={`description-${module.id}`}
                className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300"
              >
                Description
              </label>
              <textarea
                id={`description-${module.id}`}
                value={module.description}
                onChange={(e) => handleInputChange(index, "description", e)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white dark:focus:ring-indigo-400"
                rows="4"
              />
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => handleSaveModuleChanges(module.id)}
                className="rounded-md border-2 border-green-600 px-3 py-2 font-semibold text-green-600 shadow-sm hover:bg-green-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-600 flex items-center"
              >
                <MdUpload className="mr-2" /> Save
              </button>
              <button
                onClick={() => setDeleteModuleId(module.id)}
                className="rounded-md border-2 border-red-600 px-3 py-2 font-semibold text-red-600 shadow-sm hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-600 flex items-center"
              >
                <MdDelete className="mr-2" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-xl w-1/2 dark:bg-gray-800 dark:text-white">
            <h3 className="text-2xl font-bold mb-4">Add New Module</h3>
            <div className="mb-4">
              <label
                htmlFor="new-indexNo"
                className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300"
              >
                Index No
              </label>
              <input
                type="number"
                id="new-indexNo"
                value={newModule.indexNo}
                onChange={(e) =>
                  setNewModule({ ...newModule, indexNo: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white dark:focus:ring-indigo-400"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="new-heading"
                className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300"
              >
                Heading
              </label>
              <input
                type="text"
                id="new-heading"
                value={newModule.heading}
                onChange={(e) =>
                  setNewModule({ ...newModule, heading: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white dark:focus:ring-indigo-400"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="new-description"
                className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300"
              >
                Description
              </label>
              <textarea
                id="new-description"
                value={newModule.description}
                onChange={(e) =>
                  setNewModule({ ...newModule, description: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white dark:focus:ring-indigo-400"
                rows="4"
              />
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => setIsModalOpen(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleAddModule}
                className="bg-green-500 text-white px-4 py-2 rounded-md"
              >
                Add Module
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModuleId && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-xl w-1/3 dark:bg-gray-800 dark:text-white">
            <h3 className="text-xl font-semibold mb-4">
              Are you sure you want to delete this module?
            </h3>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setDeleteModuleId(null)}
                className="bg-gray-500 text-white px-4 py-2 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteModule}
                className="bg-red-500 text-white px-4 py-2 rounded-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} newestOnTop closeButton={true} />
    </>
  );
};

export default Modules;